import axiosInstance from './axiosConfig';

export interface CompanySettings {
  company_name: string;
  tagline: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  logo_url: string;
  logo_filename: string;
  tax_number: string;
  business_number: string;
  currency: string;
  currency_symbol: string;
  timezone: string;
  invoice_footer: string;
  email_signature: string;
  terms_conditions: string;
  privacy_policy: string;
}

class CompanySettingsService {
  private cache: CompanySettings | null = null;
  private cacheExpiry: number | null = null;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // Get company settings with caching
  async getCompanySettings(): Promise<CompanySettings> {
    try {
      // Check if cache is valid
      if (this.cache && this.cacheExpiry && Date.now() < this.cacheExpiry) {
        return this.cache;
      }

      const response = await axiosInstance.get('/company-settings');
      const settings = response.data;
      this.cache = settings;
      this.cacheExpiry = Date.now() + this.CACHE_DURATION;
      return settings;
    } catch (error) {

      throw error;
    }
  }

  // Get public company info (no authentication required)
  async getPublicCompanyInfo(): Promise<Partial<CompanySettings>> {
    try {
      const response = await axiosInstance.get(`/company-settings/public`);

      return response.data;
    } catch (error) {

      throw error;
    }
  }

  // Update company settings
  async updateCompanySettings(settings: Partial<CompanySettings>): Promise<CompanySettings> {
    try {
      // Clear cache
      this.cache = null;
      this.cacheExpiry = null;

      const response = await axiosInstance.put('/company-settings', settings);
      return response.data.settings;
    } catch (error) {

      throw error;
    }
  }

  // Upload company logo
  async uploadLogo(file: File): Promise<{ logoUrl: string; filename: string }> {
    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await axiosInstance.post('/company-settings/logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      const result = response.data;
      // Clear cache to force refresh
      this.cache = null;
      this.cacheExpiry = null;
      return result;
    } catch (error) {

      throw error;
    }
  }

  // Delete company logo
  async deleteLogo(): Promise<void> {
    try {
      await axiosInstance.delete('/company-settings/logo');
      // Clear cache to force refresh
      this.cache = null;
      this.cacheExpiry = null;
    } catch (error) {

      throw error;
    }
  }

  // Reset company settings to defaults
  async resetToDefaults(): Promise<CompanySettings> {
    try {
      const response = await axiosInstance.post('/company-settings/reset');
      const result = response.data;
      // Clear cache to force refresh
      this.cache = null;
      this.cacheExpiry = null;
      return result.settings;
    } catch (error) {

      throw error;
    }
  }

  // Clear cache (useful for testing or manual cache invalidation)
  clearCache(): void {
    this.cache = null;
    this.cacheExpiry = null;
  }

  // Get logo URL
  getLogoUrl(): string {
    return this.cache?.logo_url || '';
  }

  // Get company name
  getCompanyName(): string {
    return this.cache?.company_name || 'TailorHub';
  }

  // Get currency symbol
  getCurrencySymbol(): string {
    return this.cache?.currency_symbol || '$';
  }

  // Get currency code
  getCurrencyCode(): string {
    return this.cache?.currency || 'USD';
  }

  // Get current currency for formatting
  getCurrentCurrency(): string {
    return this.cache?.currency || 'KES';
  }

  // Get current currency symbol for formatting
  getCurrentCurrencySymbol(): string {
    return this.cache?.currency_symbol || 'KES';
  }

  // Format currency using current company settings
  formatCurrency(amount: number): string {
    const currency = this.getCurrentCurrency();
    const symbol = this.getCurrentCurrencySymbol();
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }
}

export const companySettingsService = new CompanySettingsService(); 